import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SectionHeading from '@/components/base/SectionHeading';
import ArticleCard from '@/components/base/ArticleCard';
import { getAllArticles, getCategories } from '@/utils/articles';

export default function LatestFeed() {
  const [active, setActive] = useState('all');
  const allArticles = getAllArticles();
  const categories = getCategories();
  const discussed = useMemo(
    () => [...allArticles].sort((a, b) => b.comments - a.comments).slice(0, 5),
    [allArticles],
  );
  const tagCloud = useMemo(() => {
    const map = new Map<string, number>();
    allArticles.forEach((item) => item.tags.forEach((tag) => map.set(tag, (map.get(tag) || 0) + 1)));
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12);
  }, [allArticles]);

  const filtered = useMemo(
    () => (active === 'all' ? allArticles : allArticles.filter((item) => item.category === active)),
    [active, allArticles],
  );

  return (
    <section id="latest" className="w-full border-t border-background-200 bg-background-100 py-10 md:py-14">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHeading
              eyebrow="TODAY UPDATE"
              title="今日爆料实时更新"
              description="按栏目筛选，快速浏览今天新增的爆料与讨论。"
            />

            <div className="mb-4 flex flex-wrap gap-2" role="tablist" aria-label="栏目筛选">
              <button
                type="button"
                role="tab"
                aria-selected={active === 'all'}
                onClick={() => setActive('all')}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  active === 'all'
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-50 text-foreground-700 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              {categories.map((category) => (
                <button
                  key={category.slug}
                  type="button"
                  role="tab"
                  aria-selected={active === category.slug}
                  onClick={() => setActive(category.slug)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    active === category.slug
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-50 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>

            {filtered.length > 0 ? (
              <div className="rounded-xl border border-background-200 bg-background-50 px-4 md:px-5">
                {filtered.map((article) => (
                  <ArticleCard key={article.id} article={article} variant="horizontal" />
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-background-200 bg-background-50 px-6 py-14 text-center text-sm text-foreground-500">
                该栏目暂无更新，去其他栏目看看吧
              </div>
            )}
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-chat-smile-2-line text-lg text-primary-500" />
                本周讨论最多
              </h3>
              <ul className="mt-3 divide-y divide-background-100">
                {discussed.map((item, index) => (
                  <li key={item.id}>
                    <ArticleCard article={item} variant="rank" rank={index + 1} />
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-price-tag-3-line text-lg text-accent-600" />
                热门标签
              </h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {tagCloud.map(([tag, count]) => (
                  <span
                    key={tag}
                    className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                  >
                    {tag} · {count}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-primary-100 bg-primary-50 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">有料要爆？</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                欢迎把身边的真实经历投稿给我们，我们会保护投稿人隐私，并在核实后发布。
              </p>
              <Link
                to="/about"
                className="mt-4 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-send-plane-fill" />
                立即投稿
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}